import { create } from "zustand";
import {
  fetchWorkspaceCreditLedger,
  fetchWorkspaceCreditUsage,
  fetchWorkspaceCreditBalance,
} from "@/services/creditService";
import {
  createWorkspaceBillingProfile,
  fetchWorkspaceBillingProfile,
  updateWorkspaceBillingProfile,
} from "@/services/workspaceService";
import {
  createCheckoutSession,
  createCustomerPortalSession,
  createTopupCheckoutSession,
  fetchCreditPacks,
  fetchWorkspaceBillingSubscription,
} from "@/services/billingPaymentService";
import {
  CNSBillingPeriodCycle,
  CNSCreditPackSummary,
  CNSInnovationActionPage,
  CNSPlanTemplateSummary,
  CNSWorkspaceCreditBalance,
  CNSWorkspaceCreditLedgerEntryPage,
  CNSWorkspaceBillingProfileAssignment,
  CNSWorkspaceBillingSubscription,
} from "@/models/cns";
import { getErrorMessage, getProblemDetailCode } from "@/types/problem-detail";
import { setBillingCheckoutIntent } from "@/utils/billingUtils";

/** Coalesce concurrent fetches per workspace (layout + NavUser + chat runtime + StrictMode). No TTL. */
const pendingBalanceByWorkspace = new Map<string, Promise<void>>();
const pendingBillingByWorkspace = new Map<string, Promise<void>>();
const pendingSubscriptionByWorkspace = new Map<string, Promise<void>>();
let pendingCreditPacks: Promise<void> | null = null;

interface CreditStore {
  balance: CNSWorkspaceCreditBalance | null;
  billingProfile: CNSWorkspaceBillingProfileAssignment | null;
  billingSubscription: CNSWorkspaceBillingSubscription | null;
  creditPacks: CNSCreditPackSummary[] | null;
  usage: CNSInnovationActionPage | null;
  ledger: CNSWorkspaceCreditLedgerEntryPage | null;
  loading: boolean;
  billingProfileLoading: boolean;
  billingSubscriptionLoading: boolean;
  creditPacksLoading: boolean;
  usageLoading: boolean;
  ledgerLoading: boolean;
  error: string | null;
  billingProfileError: string | null;
  billingSubscriptionError: string | null;
  creditPacksError: string | null;
  usageError: string | null;
  ledgerError: string | null;
  requiresPlanSelection: boolean;
  outOfCreditsModalOpen: boolean;
  lastLoadedWorkspaceId: string | null;
  lastLoadedBillingProfileWorkspaceId: string | null;
  lastLoadedBillingSubscriptionWorkspaceId: string | null;
  fetchBalance: (workspaceId: string) => Promise<void>;
  fetchBillingProfileStatus: (workspaceId: string) => Promise<void>;
  fetchBillingSubscription: (workspaceId: string) => Promise<void>;
  fetchCreditPacks: () => Promise<void>;
  startCheckoutForPlan: (
    workspaceId: string,
    planTemplatePublicId: string,
    billingPeriodCycle: CNSBillingPeriodCycle
  ) => Promise<void>;
  changePlan: (
    workspaceId: string,
    plan: CNSPlanTemplateSummary,
    billingPeriodCycle: CNSBillingPeriodCycle
  ) => Promise<{ kind: "free" | "checkout" }>;
  startTopUp: (workspaceId: string, creditPackPublicId: string) => Promise<void>;
  openCustomerPortal: (workspaceId: string) => Promise<void>;
  fetchUsage: (workspaceId: string, page?: number, size?: number) => Promise<void>;
  fetchLedger: (workspaceId: string, page?: number, size?: number) => Promise<void>;
  openOutOfCreditsModal: () => void;
  closeOutOfCreditsModal: () => void;
}

export const useCreditStore = create<CreditStore>((set, get) => ({
  balance: null,
  billingProfile: null,
  billingSubscription: null,
  creditPacks: null,
  usage: null,
  ledger: null,
  loading: false,
  billingProfileLoading: false,
  billingSubscriptionLoading: false,
  creditPacksLoading: false,
  usageLoading: false,
  ledgerLoading: false,
  error: null,
  billingProfileError: null,
  billingSubscriptionError: null,
  creditPacksError: null,
  usageError: null,
  ledgerError: null,
  requiresPlanSelection: false,
  outOfCreditsModalOpen: false,
  lastLoadedWorkspaceId: null,
  lastLoadedBillingProfileWorkspaceId: null,
  lastLoadedBillingSubscriptionWorkspaceId: null,
  fetchBalance: async (workspaceId: string) => {
    if (!workspaceId) return;
    let p = pendingBalanceByWorkspace.get(workspaceId);
    if (!p) {
      p = (async () => {
        set({ loading: true, error: null });
        try {
          const balance = await fetchWorkspaceCreditBalance(workspaceId);
          set({
            balance,
            loading: false,
            error: null,
            lastLoadedWorkspaceId: workspaceId,
          });
        } catch (error) {
          const code = getProblemDetailCode(error);
          if (code === "plan-required") {
            set({
              balance: null,
              loading: false,
              error: null,
              lastLoadedWorkspaceId: workspaceId,
            });
            return;
          }
          set({
            loading: false,
            error: getErrorMessage(error, "Failed to fetch credits"),
          });
        } finally {
          pendingBalanceByWorkspace.delete(workspaceId);
        }
      })();
      pendingBalanceByWorkspace.set(workspaceId, p);
    }
    await p;
  },
  fetchBillingProfileStatus: async (workspaceId: string) => {
    if (!workspaceId) return;
    let p = pendingBillingByWorkspace.get(workspaceId);
    if (!p) {
      p = (async () => {
        set({ billingProfileLoading: true, billingProfileError: null });
        try {
          const billingProfile = await fetchWorkspaceBillingProfile(workspaceId);
          set({
            billingProfile,
            requiresPlanSelection: false,
            billingProfileLoading: false,
            billingProfileError: null,
            lastLoadedBillingProfileWorkspaceId: workspaceId,
          });
        } catch (error) {
          const code = getProblemDetailCode(error);
          if (code === "billing-profile-not-found") {
            set({
              billingProfile: null,
              requiresPlanSelection: true,
              billingProfileLoading: false,
              billingProfileError: null,
              lastLoadedBillingProfileWorkspaceId: workspaceId,
            });
            return;
          }
          set({
            billingProfile: null,
            requiresPlanSelection: false,
            billingProfileLoading: false,
            billingProfileError: getErrorMessage(error, "Failed to fetch workspace billing profile"),
          });
        } finally {
          pendingBillingByWorkspace.delete(workspaceId);
        }
      })();
      pendingBillingByWorkspace.set(workspaceId, p);
    }
    await p;
  },
  fetchBillingSubscription: async (workspaceId: string) => {
    if (!workspaceId) return;
    let p = pendingSubscriptionByWorkspace.get(workspaceId);
    if (!p) {
      p = (async () => {
        set({ billingSubscriptionLoading: true, billingSubscriptionError: null });
        try {
          const billingSubscription = await fetchWorkspaceBillingSubscription(workspaceId);
          set({
            billingSubscription,
            billingSubscriptionLoading: false,
            billingSubscriptionError: null,
            lastLoadedBillingSubscriptionWorkspaceId: workspaceId,
          });
        } catch (error) {
          const code = getProblemDetailCode(error);
          if (code === "subscription-not-found" || code === "billing-profile-not-found") {
            set({
              billingSubscription: null,
              billingSubscriptionLoading: false,
              billingSubscriptionError: null,
              lastLoadedBillingSubscriptionWorkspaceId: workspaceId,
            });
            return;
          }
          set({
            billingSubscriptionLoading: false,
            billingSubscriptionError: getErrorMessage(
              error,
              "Failed to load billing summary"
            ),
          });
        } finally {
          pendingSubscriptionByWorkspace.delete(workspaceId);
        }
      })();
      pendingSubscriptionByWorkspace.set(workspaceId, p);
    }
    await p;
  },
  fetchCreditPacks: async () => {
    if (get().creditPacks && !get().creditPacksError) return;
    if (!pendingCreditPacks) {
      pendingCreditPacks = (async () => {
        set({ creditPacksLoading: true, creditPacksError: null });
        try {
          const creditPacks = await fetchCreditPacks();
          set({
            creditPacks,
            creditPacksLoading: false,
            creditPacksError: null,
          });
        } catch (error) {
          set({
            creditPacksLoading: false,
            creditPacksError: getErrorMessage(error, "Failed to load credit packs"),
          });
        } finally {
          pendingCreditPacks = null;
        }
      })();
    }
    await pendingCreditPacks;
  },
  startCheckoutForPlan: async (
    workspaceId: string,
    planTemplatePublicId: string,
    billingPeriodCycle: CNSBillingPeriodCycle
  ) => {
    const session = await createCheckoutSession(
      workspaceId,
      planTemplatePublicId,
      billingPeriodCycle
    );
    if (typeof window !== "undefined") {
      setBillingCheckoutIntent(workspaceId, "subscription");
      window.location.assign(session.sessionUrl);
    }
  },
  changePlan: async (
    workspaceId: string,
    plan: CNSPlanTemplateSummary,
    billingPeriodCycle: CNSBillingPeriodCycle
  ) => {
    if (plan.monthlyPriceCents === 0) {
      try {
        await createWorkspaceBillingProfile(workspaceId, plan.id);
      } catch (error) {
        const code = getProblemDetailCode(error);
        if (code === "billing-profile-already-exists") {
          await updateWorkspaceBillingProfile(workspaceId, plan.id);
        } else {
          throw error;
        }
      }
      await Promise.all([
        get().fetchBillingProfileStatus(workspaceId),
        get().fetchBillingSubscription(workspaceId),
        get().fetchBalance(workspaceId),
      ]);
      return { kind: "free" };
    }
    await get().startCheckoutForPlan(workspaceId, plan.id, billingPeriodCycle);
    return { kind: "checkout" };
  },
  startTopUp: async (workspaceId: string, creditPackPublicId: string) => {
    const session = await createTopupCheckoutSession(workspaceId, creditPackPublicId);
    if (typeof window !== "undefined") {
      setBillingCheckoutIntent(workspaceId, "topup");
      window.location.assign(session.sessionUrl);
    }
  },
  openCustomerPortal: async (workspaceId: string) => {
    const session = await createCustomerPortalSession(workspaceId);
    if (typeof window !== "undefined") {
      window.location.assign(session.portalUrl);
    }
  },
  fetchUsage: async (workspaceId: string, page = 0, size = 20) => {
    if (!workspaceId) return;
    set({ usageLoading: true, usageError: null });
    try {
      const usage = await fetchWorkspaceCreditUsage(workspaceId, page, size);
      set({ usage, usageLoading: false, usageError: null });
    } catch (error) {
      set({
        usageLoading: false,
        usageError: error instanceof Error ? error.message : "Failed to fetch usage",
      });
    }
  },
  fetchLedger: async (workspaceId: string, page = 0, size = 20) => {
    if (!workspaceId) return;
    set({ ledgerLoading: true, ledgerError: null });
    try {
      const ledger = await fetchWorkspaceCreditLedger(workspaceId, page, size);
      set({ ledger, ledgerLoading: false, ledgerError: null });
    } catch (error) {
      set({
        ledgerLoading: false,
        ledgerError: error instanceof Error ? error.message : "Failed to fetch ledger",
      });
    }
  },
  openOutOfCreditsModal: () => set({ outOfCreditsModalOpen: true }),
  closeOutOfCreditsModal: () => set({ outOfCreditsModalOpen: false }),
}));

